import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Eye } from "lucide-react";
import { api } from "@/lib/api";
import RecipientsEditor from "@/components/RecipientsEditor";
import { useAuth } from "@/context/AuthContext";

export default function DigestPanel() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [info, setInfo] = useState({ logs: [], recipients: [], schedule: "" });
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => api.get("/admin/digest/log").then(({ data }) => setInfo(data)).catch((e) => console.warn("Digest log failed", e)), []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(`Digest sent to ${data.recipients.join(", ")} ✓`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest could not be sent");
    } finally { setSending(false); }
  };

  const openPreview = async () => {
    try {
      const { data } = await api.get("/admin/digest/preview");
      setPreview(data.html);
    } catch (err) { console.warn(err); toast.error("Could not build preview"); }
  };

  const last = info.logs[0];

  return (
    <div className="panel" data-testid="digest-panel">
      <div className="map-head">
        <h3 style={{ margin: 0 }}><Mail size={16} style={{ verticalAlign: "-2px", marginRight: 8, color: "var(--joy-yellow)" }} />Weekly Email Digest</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={openPreview} style={{ padding: "8px 16px", fontSize: 13 }} data-testid="digest-preview-btn"><Eye size={13} style={{ marginRight: 5, verticalAlign: "-2px" }} />Preview</button>
          {isOwner && <button className="btn-yellow" onClick={sendNow} disabled={sending} style={{ width: "auto", padding: "8px 18px", fontSize: 13 }} data-testid="digest-send-btn"><Send size={13} style={{ marginRight: 5, verticalAlign: "-2px" }} />{sending ? "Sending…" : "Send now"}</button>}
        </div>
      </div>
      <div className="digest-meta">
        <div><span className="dm-lbl">Schedule</span><span data-testid="digest-schedule">{info.schedule || "—"}</span></div>
        <div><span className="dm-lbl">Recipients</span><span data-testid="digest-recipients">{info.recipients.length} {info.recipients.length === 1 ? "address" : "addresses"}</span></div>
        <div><span className="dm-lbl">Last sent</span><span data-testid="digest-last-sent">{last ? `${last.sent_at.slice(0, 16).replace("T", " ")} UTC · ${last.status}${last.trigger === "weekly" ? " (auto)" : ""}` : "Never"}</span></div>
      </div>
      <div className="dm-lbl" style={{ margin: "16px 0 8px" }}>Who receives the digest</div>
      {isOwner
        ? <RecipientsEditor recipients={info.recipients} onChange={(r) => setInfo((i) => ({ ...i, recipients: r }))} />
        : <div className="recip-box" data-testid="digest-recipients-readonly"><div className="recip-list">{info.recipients.map((e) => <span className="recip-chip" key={e}>{e}</span>)}</div><div className="edit-hint">Only owners can change who receives the digest.</div></div>}
      {info.logs.length > 0 && (
        <div className="digest-log" data-testid="digest-log">
          {info.logs.slice(0, 5).map((l, i) => (
            <div key={i} className={`dl-row ${l.status}`}><span>{l.sent_at.slice(0, 10)}</span><span>{l.new_pledges} pledges · {l.new_views} views</span><span className="dl-status">{l.status}{l.error ? ` — ${l.error.slice(0, 60)}` : ""}</span></div>
          ))}
        </div>
      )}
      {preview && (
        <div className="modal-overlay open" onClick={(e) => e.target.classList.contains("modal-overlay") && setPreview(null)}>
          <div className="modal-box" style={{ maxWidth: 680, padding: 0, overflow: "hidden", background: "#F6EEF0" }}>
            <iframe title="Digest preview" srcDoc={preview} style={{ width: "100%", height: "78vh", border: 0 }} data-testid="digest-preview-frame" />
            <button className="btn-close" onClick={() => setPreview(null)} style={{ margin: 10 }} data-testid="digest-preview-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
